import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotifications } from '../../hooks/useNotifications';
import usePrevious from '../../hooks/usePrevious';
import { selectAudioV2Status } from '../../selectors/audioV2';
import {
  selectConversationMuted,
  selectIsAvaMicAvailable,
  selectUserRoleInConversation,
  UserRoleInConversation,
} from '../../selectors/combined';
import { selectInTwilioCalls, selectIsInConversation } from '../../selectors/conversation';
import { selectStatusAudioStreams } from '../../selectors/legacy-conversation';
import { selectScribeTrainingRequested } from '../../selectors/scribe-dashboard';
import { selectAvaId } from '../../selectors/userProfile';
import { selectConferenceCallRequested } from '../../selectors/v1Session';
import { getCurrentAvailableMics, RecordingStatus, startRecording, stopRecording } from '../../store/slices/audioV2';
import { useAppDispatch, useAppSelector } from '../../store/store';

const Muter = () => {
  // This component is responsible for muting and restarting the audio under certain conditions
  // which I do not understand. It contains a set of hooks that have been moved
  // from ConversationPage in order to make it render less often. For product
  // explanation please consult JB.

  const dispatch = useAppDispatch();

  const inTwilioCalls = useAppSelector(selectInTwilioCalls);
  const conferenceCallRequested = useAppSelector(selectConferenceCallRequested);
  const userRoleIsParticipant = useAppSelector(selectUserRoleInConversation) === UserRoleInConversation.PARTICIPANT;
  const isInConversation = useAppSelector(selectIsInConversation);
  const muted = useAppSelector(selectConversationMuted);
  const scribeTrainingRequested = useAppSelector(selectScribeTrainingRequested);
  const audioStreams = useAppSelector(selectStatusAudioStreams);
  const recordingStatus = useAppSelector(selectAudioV2Status);
  const avaMicAvailable = useAppSelector(selectIsAvaMicAvailable);
  const { t } = useTranslation();
  const { notify } = useNotifications();
  const avaId = useAppSelector(selectAvaId);

  useEffect(() => {
    if (inTwilioCalls) {
      dispatch(stopRecording());
    }
  }, [inTwilioCalls]);

  useEffect(() => {
    // Conversation's audio streams can be updated remotely and backend sends this info
    // via 'room-status-update' message. Here we are interested if our audio has been
    // muted remotely.
    //
    // There could be more than one stream for each participant. We check the last (latest)
    // for this user.
    const myStreams = (audioStreams || []).filter((stream) => stream.id === avaId);
    if (myStreams.length > 0) {
      const [last] = myStreams.slice(-1);
      if (last.forceMuted && recordingStatus === RecordingStatus.RECORDING) {
        dispatch(stopRecording());
        notify(t('conversation.notifications.youHaveBeenMuted'));
      }
    }
  }, [audioStreams]);

  const prevMuted = usePrevious(muted);
  useEffect(() => {
    // If the conversation is ongoing, and we flipped from muted to unmuted
    // we potentially need to start recording. `muted` depends on
    // conversationMode.
    if (
      isInConversation &&
      !conferenceCallRequested &&
      prevMuted &&
      !muted &&
      (userRoleIsParticipant || scribeTrainingRequested) &&
      !inTwilioCalls
    ) {
      dispatch(startRecording());
    }
  }, [muted, isInConversation]);

  useEffect(() => {
    dispatch(getCurrentAvailableMics());
  }, [avaMicAvailable]);

  return null;
};

export default Muter;
